<template>
  <div class="document-container" style="padding-top: 0">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">LEA List</div>
          </template>
          <template #actions>
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <p class="control">
                    <popper
                        ref="filter"
                        trigger="click"
                        :options="{
                          placement: 'bottom-end',
                          modifiers: { offset: { offset: '0,10px' } }
                          }">
                        <div class="popper">
                          <div class="content">
                            <div class="filter-container">
                              <form>
                                <label class="label has-text-left">Population Status</label>
                                <div v-for="f in filter" :key="f.order" class="control">
                                  <label class="checkbox has-text-left">
                                    <input type="checkbox"
                                      style="margin-right:0.5rem"
                                      :value="f.code"
                                      :checked="lpStatus.includes(f.code)"
                                      @click="updatePopFilter('population', f.code)">
                                    {{f.label}}
                                  </label>
                                </div>
                                <label class="label has-text-left">Election Status</label>
                                <div v-for="f in filter" :key="f.order" class="control">
                                  <label class="checkbox has-text-left">
                                    <input type="checkbox"
                                      style="margin-right:0.5rem"
                                      :value="f.code"
                                      :checked="leStatus.includes(f.code)"
                                      @click="updatePopFilter('election', f.code)">
                                    {{f.label}}
                                  </label>
                                </div>
                                <div class="dropdown-divider"></div>
                                <div class="has-text-right">
                                  <a @click.prevent="clearPopFilter">Reset</a>
                                </div>
                              </form>
                            </div>
                          </div>
                      </div>
                      <a class="button-filter" slot="reference">
                        <span class="icon is-small">
                          <font-awesome-icon :icon="['fal', 'filter']"/>
                        </span>
                      </a>
                    </popper>
                  </p>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <div class="content">
        <table class="table is-hoverable is-fullwidth">
          <es-sorter
            :apiSorter="apiSorter"
            :columnHeaders="columnHeaders"
            @updateSorter="updateSorter"
          />
          <tbody v-if="loadingError">
            <tr>
              <td colspan="100%" style="text-align: center">
                <es-exception
                  :exceptionMessage="exceptionMessage"/>
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="loading">
            <tr>
              <td colspan="100%" style="text-align: center; padding: 6rem;">
                <div class="icon">
                  <es-loader/>
                </div>
                <p></p>
                <h4>Loading population data...</h4>
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="!locations.length">
            <tr>
              <td colspan="100%" style="text-align: center">
                <es-exception exceptionMessage="No results found."/>
              </td>
            </tr>
          </tbody>
           <template v-else>
            <tbody>
              <tr v-for="location in locations" :key="location.locationGroupId">
                <td>
                  <es-eligibility-marker size="small" :value="location.totalISP" />
                </td>
                <td>{{ location.locationName }}</td>
                <td>
                  {{ `${location.identifiedCount || 'n/a '}/${location.enrolledCount || ' n/a'}` }}
                </td>
                <td>{{ location.totalISP ? (location.totalISP * 100).toFixed(2) : '-'}}</td>
                <td>
                  <!-- TODO: cleanup this section. Call computd prop. -->
                  <span
                    v-if="location.populationStatus === 'S'"
                    class="text-nowrap"
                    title="Submitted">
                    <font-awesome-icon :icon="['fas', 'dot-circle']" class="has-text-info"/>
                   {{ cepStatuses[location.populationStatus] }}
                  </span>
                  <span
                    v-if="location.populationStatus === 'P'"
                    class="text-nowrap"
                    title="Pending">
                    <font-awesome-icon
                      :icon="['fas', 'exclamation-circle']"
                      class="has-text-warning"/>
                   {{ cepStatuses[location.populationStatus] }}
                  </span>
                  <span
                    v-if="location.populationStatus === 'A'"
                    class="text-nowrap"
                    title="Accepted">
                    <font-awesome-icon :icon="['fas', 'check-circle']" class="has-text-success"/>
                    {{ cepStatuses[location.populationStatus] }}
                  </span>
                  <span
                    v-if="location.populationStatus === 'R'"
                    class="text-nowrap"
                    title="Rejected">
                    <font-awesome-icon :icon="['fas', 'ban']" class="has-text-danger"/>
                    {{ cepStatuses[location.populationStatus] }}
                  </span>
                  <span
                    v-if="location.populationStatus === 'D'"
                    class="text-nowrap has-text-weight-bold"
                    :class="{'has-text-danger' : location.populationStatus === 'D'}"
                    title="Due">
                    {{ location.electionStatus === 'D'
                        ? populationDueDate (location) : cepStatuses[location.populationStatus] }}
                  </span>
                  <span
                    v-if="location.populationStatus === 'O'"
                    class="text-nowrap"
                    title="Reopen">
                    {{ cepStatuses[location.populationStatus] }}
                  </span>
                </td>
                <td>
                  <!-- TODO: cleanup this section. Call computd prop. -->
                  <span
                    class="text-nowrap"
                    title="Submitted"
                    v-if="location.electionStatus === 'S'">
                    <font-awesome-icon :icon="['fas', 'dot-circle']" class="has-text-info"/>
                    {{ cepStatuses[location.electionStatus] }}
                  </span>
                  <span
                    class="text-nowrap"
                    title="Pending"
                    v-if="location.electionStatus === 'P'">
                    <font-awesome-icon
                      :icon="['fas', 'exclamation-circle']"
                      class="has-text-warning"/>
                    {{ cepStatuses[location.electionStatus] }}
                  </span>
                  <span
                    class="text-nowrap"
                    title="Accepted"
                    v-if="location.electionStatus === 'A'">
                    <font-awesome-icon :icon="['fas', 'check-circle']" class="has-text-success"/>
                    {{ cepStatuses[location.electionStatus] }}
                  </span>
                  <span
                    class="text-nowrap"
                    title="Rejected"
                    v-if="location.electionStatus === 'R'">
                    <font-awesome-icon :icon="['fas', 'ban']" class="has-text-danger"/>
                    {{ cepStatuses[location.electionStatus] }}
                  </span>
                  <span
                    class="text-nowrap"
                    title="Reopen"
                    v-if="location.electionStatus === 'O'">
                    {{ cepStatuses[location.electionStatus] }}
                  </span>
                  <span
                    v-if="location.electionStatus === 'D'"
                    class="text-nowrap has-text-weight-bold"
                    :class="{'has-text-danger' : location.electionStatus === 'D'}"
                    title="Due">
                    {{ location.electionStatus === 'D'
                        ? electionsDueDate (location) : cepStatuses[location.electionStatus] }}
                  </span>
                </td>
                <td class="has-text-weight-bold">
                  {{ location.oneYearRenewal ? 'Enabled' : 'Disabled' }}
                </td>
                <td>
                    <div class="is-flex flex-align--center border-left">
                    <div v-if="currentSchoolYear === schoolYear && popupMenu(location).length>0"
                      class="action-padding">
                      <a class="padding-left--1rem">
                        <es-menu
                          :setValues="popupMenu(location)"
                          @callMenuAction="popUpMenuAction(location)"/>
                      </a>
                    </div>
                    <div class="action-padding">
                      <router-link
                        :to="{
                          path: `/directMatch/cep/population`,
                          query: {
                            locationGroupId: location.locationGroupId,
                            schoolYear,
                          },
                        }"
                        title="View Details"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'angle-double-right']"
                          size="2x"
                          title="View Details"/>
                      </router-link>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="100%">
                  <es-pager :totalCount="totalCount"/>
                </td>
              </tr>
            </tfoot>
           </template>
        </table>
      </div>
    </div>
    <cep-legend />
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import moment from 'moment';
import Popper from 'vue-popperjs';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faExclamationTriangle,
  faFilter,
} from '@fortawesome/pro-light-svg-icons';
import {
  faCheckCircle,
  faDotCircle,
  faExclamationCircle,
  faSyncAlt,
  faCheck,
  faBan,
  faAngleDoubleRight,
} from '@fortawesome/pro-solid-svg-icons';

import CepLegend from '@/components/CepLegend.vue';
import EsEligibilityMarker from '@/components/EsEligibilityMarker.vue';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSorter from '@/components/EsSorter.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import EsMenu from '@/components/EsMenu.vue';

library.add(
  faAngleDoubleRight,
  faBan,
  faCheck,
  faCheckCircle,
  faDotCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faSyncAlt,
  faFilter,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    CepLegend,
    EsEligibilityMarker,
    EsException,
    EsHeader,
    EsLoader,
    EsPager,
    EsSorter,
    EsMenu,
    Popper,
  },
  props: {
    cepSummary: {
      type: Object,
    },
    loadingSummary: {
      type: Boolean,
    },
    loadingSummaryError: {
      type: Boolean,
    },
    locationGroupId: {
      type: [String, Number],
    },
    page: {
      type: Number,
      default: 1,
    },
    schoolYear: {
      type: Number,
    },
    sortField: {
      type: String,
      default: 'groupName',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
    populationStatus: {
      type: Array,
      default: () => [],
    },
    electionStatus: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      locations: [],
      totalCount: null,
      apiSorter: {},
      columnHeaders: [],
      cepStatuses: {
        S: 'Submitted',
        O: 'Reopen',
        A: 'Accepted',
        D: 'Due',
        P: 'Pending',
        R: 'Rejected',
      },
      filter: [
        { code: 'A', label: 'Accepted' },
        { code: 'S', label: 'Submitted' },
        { code: 'O', label: 'Reopen' },
      ],
      lpStatus: this.populationStatus,
      leStatus: this.electionStatus,
      exceptionMessage: 'There was an issue. Please try again or contact your administrator.',
    };
  },
  created() {
    this.getLocations();

    this.columnHeaders = [
      {
        header: null,
        field: null,
      },
      {
        header: 'Location Name',
        field: 'groupName',
        order: 'ASC',
        sortable: true,
      },
      {
        header: 'Identified/Enrolled',
        field: 'identifiedCount',
        order: null,
        sortable: true,
      },
      {
        header: 'ISP %',
        field: 'totalISP',
        order: null,
        sortable: true,
      },
      {
        header: 'Population Status',
        field: 'populationStatus',
        order: null,
        sortable: true,
      },
      {
        header: 'Election Status',
        field: 'electionStatus',
        order: null,
        sortable: true,
      },
      {
        header: 'Grace Year',
        field: 'oneYearRenewal',
        order: null,
        sortable: true,
      },
      {
        header: 'Actions',
        field: null,
      },
    ];
  },
  watch: {
    $route() {
      this.getLocations();
    },
  },
  computed: {
    ...mapGetters({
      isStateUser: 'CEPPopulationApprovalUser',
      properties: 'propertiesModule/properties',
    }),
    currentSchoolYear() {
      return Number(this.$store.state.propertiesModule.properties['CEP-CEP_CURRENT_SCHOOL_YEAR']);
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    searchParams() {
      const params = {};
      if (this.schoolYear) {
        params.schoolYear = this.schoolYear;
      }
      if (this.lpStatus && this.lpStatus.length) {
        params.populationStatus = this.lpStatus;
      }
      if (this.leStatus && this.leStatus.length) {
        params.electionStatus = this.leStatus;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      } else {
        params.sortField = this.sortField;
        params.sortOrder = this.sortOrder;
      }
      return params;
    },
    urlParams() {
      const params = {};
      params.pageNum = this.page ? this.page : 1;
      params.populationStatus = this.lpStatus;
      params.electionStatus = this.leStatus;
      const sorters = {};
      if (this.getSorter) {
        sorters.field = this.getSorter.field;
        sorters.order = this.getSorter.order;
      } else {
        sorters.field = this.sortField;
        sorters.order = this.sortOrder;
      }
      const sort = [];
      sort.push(sorters);
      params.sort = JSON.stringify(sort);
      return params;
    },
  },
  methods: {
    async getLocations() {
      try {
        this.loading = true;
        this.loadingError = false;
        const response = await axios
          .get(`cep/location-groups/${this.schoolYear}/summary`,
            {
              params: this.urlParams,
              paramsSerializer(params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
              },
            });

        this.locations = response.data.data;
        this.totalCount = response.data.totalCount;
        [this.apiSorter] = response.data.sorters;
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
    electionsDueDate(location) {
      return `${this.cepStatuses[location.electionStatus]}
        ${moment(location.electionsDueDate).format('MM/DD')}`;
    },
    populationDueDate(location) {
      return `${this.cepStatuses[location.populationStatus]}
        ${moment(location.populationDueDate).format('MM/DD')}`;
    },
    popupMenu(location) {
      const items = [];
      if (this.hasModule('DRMCH:CEP:GRACEYEAR:UPDATE')) {
        items.push({
          id: 0,
          description: `${location.oneYearRenewal ? 'Disable' : 'Enable'} Grace Year`,
          isInternal: true,
          internalAction: 'popUpMenuAction',
        });
      }
      return items;
    },
    async popUpMenuAction(location) {
      try {
        await axios.put(`cep/location-groups/${location.locationGroupId}?oneYearRenewal=${location.oneYearRenewal !== true}`);
        this.showNotification('success', ['Location is updated.']);
        this.getLocations();
      } catch (error) {
        const { status } = error.response;
        if (status === 400) {
          this.showNotification('danger', [`There was a problem updating location. ${error.response.data.message}`]);
        } else {
          this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
        }
      }
    },
    clearPopFilter() {
      this.$set(this, 'lpStatus', []);
      this.$set(this, 'leStatus', []);
      this.$router.replace({ query: this.searchParams });
    },
    updatePopFilter(type, code) {
      if (type === 'population') {
        this.$set(this, 'lpStatus', this.addOrRemove(this.lpStatus, code));
        this.$router.replace({ query: this.searchParams });
      }
      if (type === 'election') {
        this.$set(this, 'leStatus', this.addOrRemove(this.leStatus, code));
        this.$router.replace({ query: this.searchParams });
      }
    },
    addOrRemove(filters, code) {
      let arr = filters;
      if (arr.includes(code)) {
        arr = arr.filter(item => item !== code);
      } else {
        arr.push(code);
      }
      return arr;
    },
  },
};
</script>

<style lang="sass" scoped>
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .certify-row
    justify-content: flex-end
    align-items: center
  .certify-description
    margin-right: 1rem
  .action-padding
    padding-left: 0.5rem
</style>
